import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Differentiation from './components/Differentiation';
import HumanInTheLoop from './components/HumanInTheLoop';
import SocialProof from './components/SocialProof';
import ProblemSolution from './components/ProblemSolution';
import AIFeatures from './components/AIFeatures';
import HowItWorks from './components/HowItWorks';
import PlatformFeatures from './components/PlatformFeatures';
import UseCases from './components/UseCases';
import ROISection from './components/ROISection';
import About from './components/About';
import Team from './components/Team';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import Pricing from './components/Pricing';
import ScrollToTop from './components/ScrollToTop';
import LeadCapturePopup from './components/LeadCapturePopup';
import { OrganizationSchema, SoftwareApplicationSchema } from './components/Schema';

const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const HipaaCompliance = lazy(() => import('./pages/HipaaCompliance'));
const Security = lazy(() => import('./pages/Security'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Register = lazy(() => import('./pages/Register'));
const Therapists = lazy(() => import('./pages/use-cases/Therapists'));
const Clinics = lazy(() => import('./pages/use-cases/Clinics'));
const GroupPractices = lazy(() => import('./pages/use-cases/GroupPractices'));
const APCM = lazy(() => import('./pages/APCM'));
const Connect = lazy(() => import('./pages/Connect'));
const NotFound = lazy(() => import('./pages/NotFound'));

function ScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 0);
      }
    }
  }, [hash]);
  return null;
}

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 rounded-full border-2 border-brand-200 border-t-brand-600 animate-spin" />
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen">
      <title>Doctorite AI — AI-Powered Behavioral Health Platform</title>
      <meta name="description" content="The only behavioral health platform with real-time AI session guidance. Reduce documentation time by 70% with an EHR built for therapists, clinics, and group practices." />
      <OrganizationSchema />
      <SoftwareApplicationSchema />
      <Navbar />
      <main>
        <Hero />
        <Differentiation />
        <SocialProof />
        <ProblemSolution />
        <AIFeatures />
        <HumanInTheLoop />
        <HowItWorks />
        <PlatformFeatures />
        <Pricing />
        <UseCases />
        <ROISection />
        <About />
        <Team />
        <ContactForm />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <ScrollToHash />
      <LeadCapturePopup />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/register" element={<Register />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/use-cases/therapists" element={<Therapists />} />
          <Route path="/use-cases/clinics" element={<Clinics />} />
          <Route path="/use-cases/group-practices" element={<GroupPractices />} />
          <Route path="/apcm" element={<APCM />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/hipaa-compliance" element={<HipaaCompliance />} />
          <Route path="/security" element={<Security />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
